import React, { useState, useCallback, useMemo } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { CommonProperties, CommonStyles } from '../../../styles';
import { useFocusEffect, useNavigation, useRoute } from '@react-navigation/native';
import { Modals } from '../../../constants/ScreenNames';
import Camera from '../../Camera';
import { scaleVertically } from '../../../utils/scale.utils';
import ExpenseCar from '../../ExpenseWidget/ExpenseCar';
import { isNullOrEmpty } from '../../../utils/common.utils';
import { useSelector } from 'react-redux';
import { selectCurrentExpenseReportCardModalItem } from '../../../store/selectors/expense.selector';
import { EXPENSE_ACTION_TYPES } from '../../../constants/Enums';
import {
  getRefinedAndHighestConfidenceData,
  shouldUpdateExpenseState,
} from '../../../utils/expense.utils';
import useExpenseHeaderSetup from '../../../utils/useExpenseHeaderSetup';
import { PDF_MIME_TYPE } from '../../../utils/image.utils';
import useExpenseFormStepOne from '../../ExpenseWidget/useExpenseFormStepOne';

const determineExpenseMode = (
  selectedCharge,
  item,
  selectedReceipts,
  fromExistingReport = false
) => {
  if (!isNullOrEmpty(selectedCharge)) {
    return {
      isCreationForm: true,
      data: { ...selectedCharge, Amount: selectedCharge.ChargeAmount },
      fromExistingReport,
    };
  }

  if (!isNullOrEmpty(item)) {
    return {
      isCreationForm: false,
      data: item,
      fromExistingReport: false,
    };
  }

  if (!isNullOrEmpty(selectedReceipts)) {
    const data = getRefinedAndHighestConfidenceData(selectedReceipts);
    return {
      isCreationForm: true,
      data,
      fromExistingReport,
    };
  }

  return { isCreationForm: true, data: {}, fromExistingReport };
};

const CreateExpenseModal = () => {
  const navigation = useNavigation();
  const currentExpenseReportCardModalItem = useSelector(selectCurrentExpenseReportCardModalItem);
  const [enableCamera, setEnableCamera] = useState(false);
  const route = useRoute();
  const {
    isCarMileageExpense = false,
    selectedCharge,
    isEditable = true,
    item,
    file = null,
    totalCharges = null,
    currentChargeIndex = null,
    selectedReceipts = null,
    fromReceiptsTab = false,
    subTab,
    firstExpenseReportId = null,
  } = route.params || {};
  // Added useMemo to avoid ExpenseFormStepOne re-rendering
  const expenseMode = useMemo(
    () =>
      determineExpenseMode(
        selectedCharge,
        item,
        selectedReceipts,
        route.params?.fromExistingReport
      ),
    [selectedCharge, item, selectedReceipts, route.params.fromExistingReport]
  );
  const { isCreationForm, data, fromExistingReport } = expenseMode;
  const actionType = isCreationForm ? EXPENSE_ACTION_TYPES.CREATION : EXPENSE_ACTION_TYPES.UPDATE;

  useFocusEffect(
    useCallback(() => {
      if (isNullOrEmpty(file)) {
        return;
      }
      // Spread existing params and only override 'file'
      navigation.setParams({
        ...route.params,
        file: null,
      });
      navigation.navigate(Modals.ReceiptScannerModal, {
        file: file.base64,
        actionType,
        subTab,
        isPDF: file.isPDF,
      });
    }, [route.params])
  );

  useExpenseHeaderSetup({
    navigation,
    reportNumber: currentExpenseReportCardModalItem?.ReportNumber,
    fromExistingReport,
    isCreationForm,
  });

  const { stepOneForm, stepOneContinueButton } = useExpenseFormStepOne({
    isCreationForm: isNullOrEmpty(item),
    subTab,
    selectedCharge,
    isEditable,
    existingExpense: item,
    fromExistingReport,
    fromReceiptsTab,
    selectedReceipts,
    receiptsData: data,
    totalCharges,
    currentChargeIndex: currentChargeIndex,
    continueButtonStyle: styles.submitButton,
    isMileage: isCarMileageExpense,
    firstExpenseReportId,
  });

  return (
    <>
      <ScrollView style={styles.container}>
        {isCarMileageExpense ? (
          <View style={CommonStyles.mx4}>
            <ExpenseCar
              stepOneForm={stepOneForm}
              stepOneContinueButton={stepOneContinueButton}
              isCreationForm={isCreationForm}
              isEditable={isEditable}
              subTab={subTab}
            />
          </View>
        ) : (
          <View style={CommonStyles.mx4}>{stepOneForm}</View>
        )}
        {enableCamera ? (
          <Camera
            showRotate
            showGallery
            onClose={() => setEnableCamera(false)}
            onGetImage={(data) => {
              setEnableCamera(false);
              navigation.navigate(Modals.ReceiptScannerModal, {
                file: data.base64,
                actionType,
                subTab,
                updateExpenseState: shouldUpdateExpenseState(isCreationForm, selectedCharge),
                isPDF: data.mimeType === PDF_MIME_TYPE,
              });
            }}
            isExpense={true}
          />
        ) : null}
      </ScrollView>
      {stepOneContinueButton}
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    ...CommonProperties.defaultContainer,
  },
  submitButton: {
    ...CommonProperties.py2,
    ...CommonProperties.shadow,
    ...CommonProperties.borderRadius4,
    ...CommonProperties.mx4,
    ...CommonProperties.mb4,
    bottom: scaleVertically(15),
    position: 'absolute',
    left: 0,
    right: 0,
  },
});

export default CreateExpenseModal;
